// src/utils/rawMaterialModel.js

// 🧱 Raw Material Schema
export const RawMaterialSchema = {
  _id: '',
  name: '',
  code: '',
  unit: '', // Reference to unit ID
  threshold: 0,
  description: '',
  isActive: true,
  createdAt: null,
  updatedAt: null
}

// 🛡️ Validation Rules
export const VALIDATION_RULES = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s\-_.,()]+$/,
    message: 'Name can only contain letters, numbers, spaces, and basic punctuation'
  },
  code: {
    required: true,
    minLength: 2,
    maxLength: 20,
    pattern: /^[A-Z0-9\-_]+$/,
    message: 'Code must be uppercase letters, numbers, hyphens, or underscores only'
  },
  unit: {
    required: true,
    message: 'Unit is required'
  },
  threshold: {
    required: true,
    min: 0,
    max: 999999,
    message: 'Threshold must be a positive number between 0 and 999999'
  },
  description: {
    required: true,
    minLength: 1,
    maxLength: 500,
    message: 'Description must be less than 500 characters'
  }
}

// 🏭 Raw Material Model Class
export class RawMaterialModel {
  
  // Create a new raw material instance
  static create(data = {}) {
    return {
      ...RawMaterialSchema,
      ...data,
      _id: data._id || `temp_${Date.now()}`,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString()
    }
  }
  
  // Validate raw material data
  static validate(rawMaterialData) {
    const errors = {}
    
    // Validate name
    if (!rawMaterialData.name || !rawMaterialData.name.trim()) {
      errors.name = 'Material name is required'
    } else if (rawMaterialData.name.length < VALIDATION_RULES.name.minLength) {
      errors.name = `Name must be at least ${VALIDATION_RULES.name.minLength} characters`
    } else if (rawMaterialData.name.length > VALIDATION_RULES.name.maxLength) {
      errors.name = `Name must be less than ${VALIDATION_RULES.name.maxLength} characters`
    } else if (!VALIDATION_RULES.name.pattern.test(rawMaterialData.name)) {
      errors.name = VALIDATION_RULES.name.message
    }
    
    // Validate code
    if (!rawMaterialData.code || !rawMaterialData.code.trim()) {
      errors.code = 'Material code is required'
    } else if (rawMaterialData.code.length < VALIDATION_RULES.code.minLength) {
      errors.code = `Code must be at least ${VALIDATION_RULES.code.minLength} characters`
    } else if (rawMaterialData.code.length > VALIDATION_RULES.code.maxLength) {
      errors.code = `Code must be less than ${VALIDATION_RULES.code.maxLength} characters`
    } else if (!VALIDATION_RULES.code.pattern.test(rawMaterialData.code)) {
      errors.code = VALIDATION_RULES.code.message
    }
    
    // Validate unit
    if (!rawMaterialData.unit || !rawMaterialData.unit.trim()) {
      errors.unit = VALIDATION_RULES.unit.message
    }
    
    // Validate threshold
    if (rawMaterialData.threshold === undefined || rawMaterialData.threshold === null || rawMaterialData.threshold === '') {
      errors.threshold = 'Threshold is required'
    } else {
      const threshold = Number(rawMaterialData.threshold)
      if (isNaN(threshold)) {
        errors.threshold = 'Threshold must be a valid number'
      } else if (threshold < VALIDATION_RULES.threshold.min) {
        errors.threshold = `Threshold must be at least ${VALIDATION_RULES.threshold.min}`
      } else if (threshold > VALIDATION_RULES.threshold.max) {
        errors.threshold = VALIDATION_RULES.threshold.message
      }
    }
    
    // Validate description (required)
    if (!rawMaterialData.description || !rawMaterialData.description.trim()) {
      errors.description = 'Description is required'
    } else if (rawMaterialData.description.length < VALIDATION_RULES.description.minLength) {
      errors.description = 'Description cannot be empty'
    } else if (rawMaterialData.description.length > VALIDATION_RULES.description.maxLength) {
      errors.description = VALIDATION_RULES.description.message
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  // Validate partial data for updates
  static validatePartial(rawMaterialData) {
    const errors = {}
    
    // Only validate fields that are provided
    if (rawMaterialData.name !== undefined) {
      if (!rawMaterialData.name || !rawMaterialData.name.trim()) {
        errors.name = 'Material name cannot be empty'
      } else if (!VALIDATION_RULES.name.pattern.test(rawMaterialData.name)) {
        errors.name = VALIDATION_RULES.name.message
      }
    }
    
    if (rawMaterialData.code !== undefined) {
      if (!rawMaterialData.code || !rawMaterialData.code.trim()) {
        errors.code = 'Material code cannot be empty'
      } else if (!VALIDATION_RULES.code.pattern.test(rawMaterialData.code)) {
        errors.code = VALIDATION_RULES.code.message
      }
    }
    
    if (rawMaterialData.unit !== undefined) {
      if (!rawMaterialData.unit || !rawMaterialData.unit.trim()) {
        errors.unit = VALIDATION_RULES.unit.message
      }
    }
    
    if (rawMaterialData.threshold !== undefined) {
      const threshold = Number(rawMaterialData.threshold)
      if (isNaN(threshold) || threshold < 0) {
        errors.threshold = VALIDATION_RULES.threshold.message
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  // Format raw material for display
  static formatDisplay(rawMaterial, unit = null) {
    if (!rawMaterial) return 'Unknown Material'
    
    const unitText = unit ? ` (${unit.abbreviation})` : ''
    return `${rawMaterial.name} - ${rawMaterial.code}${unitText}`
  }
  
  // Get status badge classes
  static getStatusBadgeClasses(isActive) {
    return isActive
      ? 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'
      : 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800'
  }
  
  // Get status label
  static getStatusLabel(isActive) {
    return isActive ? 'Active' : 'Inactive'
  }
  
  // Check if material is low stock
  static isLowStock(currentStock, threshold) {
    return currentStock <= threshold
  }
  
  // Format threshold display
  static formatThreshold(threshold, unit = null) {
    const unitText = unit ? ` ${unit.abbreviation}` : ''
    return `${threshold}${unitText}`
  }
  
  // Search and filter methods
  static search(materials, searchTerm) {
    if (!searchTerm) return materials
    
    const term = searchTerm.toLowerCase()
    return materials.filter(material => 
      material.name?.toLowerCase().includes(term) ||
      material.code?.toLowerCase().includes(term) ||
      material.description?.toLowerCase().includes(term)
    )
  }
  
  static filterByStatus(materials, isActive) {
    if (isActive === null || isActive === undefined) return materials
    return materials.filter(material => material.isActive === isActive)
  }
  
  static filterByLowStock(materials, stockData = []) {
    return materials.filter(material => {
      const stock = stockData.find(s => s.materialId === material._id)
      return stock ? this.isLowStock(stock.quantity, material.threshold) : false
    })
  }
  
  // Sort materials
  static sort(materials, sortBy = 'name', sortOrder = 'asc') {
    return [...materials].sort((a, b) => {
      let valueA = a[sortBy]
      let valueB = b[sortBy]
      
      // Handle string comparison
      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase()
        valueB = valueB.toLowerCase()
      }
      
      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
      }
    })
  }
  
  // Analytics methods
  static getSummary(materials, stockData = []) {
    const summary = {
      total: materials.length,
      active: materials.filter(m => m.isActive).length,
      inactive: materials.filter(m => !m.isActive).length,
      lowStock: 0,
      outOfStock: 0,
      averageThreshold: 0
    }
    
    // Calculate low stock and out of stock
    materials.forEach(material => {
      const stock = stockData.find(s => s.materialId === material._id)
      if (stock) {
        if (stock.quantity === 0) {
          summary.outOfStock++
        } else if (this.isLowStock(stock.quantity, material.threshold)) {
          summary.lowStock++
        }
      }
    })
    
    // Calculate average threshold
    if (materials.length > 0) {
      const totalThreshold = materials.reduce((sum, m) => sum + (m.threshold || 0), 0)
      summary.averageThreshold = Math.round(totalThreshold / materials.length)
    }
    
    return summary
  }
  
  // Export/Import helpers
  static toCSV(materials, units = []) {
    const headers = ['Name', 'Code', 'Unit', 'Threshold', 'Description', 'Status']
    const rows = materials.map(material => {
      const unit = units.find(u => u._id === material.unit)
      return [
        material.name,
        material.code,
        unit ? unit.name : 'Unknown',
        material.threshold,
        material.description || '',
        material.isActive ? 'Active' : 'Inactive'
      ]
    })
    
    return [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n')
  }
  
  // Create default raw material object
  static createDefault() {
    return this.create()
  }
}

export default RawMaterialModel